'use client';

import { useMemo } from 'react';
import { useQueries } from '@tanstack/react-query';
import { useAuthUser } from '@/lib/auth/common/useSession';
import { fetchLearningPathProgress } from '@/lib/api/get-user-learning-paths';
import { UserLearningPath } from '@/types/learning-paths';
import {
  computeLpAssessmentUnlocked,
  getLearningPathCertificateLabel,
} from '@/lib/utils/learning-path-utils';
import { getCoursesWithQuizCount, getVideosCountFromCourses } from '@/lib/utils/learning-path-counts';
import { LearningPathSection } from './LearningPathSection';
import type { LearningPathData } from './LearningPathSection';

interface SliderData {
  title?: string;
  link?: {
    title?: string;
    href?: string;
  };
  cards?: any[];
}

interface LearningPathSectionClientProps {
  slider?: SliderData;
  learningPaths?: LearningPathData[];
}

// Helper function to extract UIDs from slider data
function extractLearningPathUIDs(slider?: SliderData): string[] {
  if (!slider?.cards) return [];
  
  return slider.cards
    .map((cardItem: any) => {
      const learningPath = cardItem?.card?.[0];
      return learningPath?.uid;
    })
    .filter((uid): uid is string => !!uid);
}

export function LearningPathSectionClient({
  slider,
  learningPaths: propLearningPaths = [],
}: LearningPathSectionClientProps) {
  // Reactive to login/logout (userDataUpdated/userLoggedOut events via SessionHydrator) —
  // a useState seeded once from isUserLoggedIn() only reflected auth state as of mount.
  const { isLoggedIn } = useAuthUser();

  // Extract learning path UIDs from slider
  const learningPathUIDs = useMemo(() => {
    if (slider) {
      return extractLearningPathUIDs(slider);
    }
    return [];
  }, [slider]);

  // Fetch progress for all learning paths in parallel using useQueries
  const progressQueries = useQueries({
    queries: learningPathUIDs.length > 0 && isLoggedIn
      ? learningPathUIDs.map((uid) => ({
          queryKey: ['learning-path-progress', uid],
          queryFn: async () => {
            try {
              return await fetchLearningPathProgress(uid);
            } catch (error: any) {
              // Return null for 404 (not enrolled) or other errors
              if (error?.response?.status === 404) {
                return null;
              }
              // Return null for errors
              return null;
            }
          },
          enabled: isLoggedIn && !!uid,
          staleTime: 0,
          refetchOnMount: true,
          retry: false, // Don't retry on 404 (not enrolled)
        }))
      : [],
  });

  // Create a map of UID to progress data
  const progressMap = useMemo(() => {
    const map = new Map<string, UserLearningPath>();
    if (progressQueries && Array.isArray(progressQueries)) {
      progressQueries.forEach((query, index) => {
        const uid = learningPathUIDs[index];
        if (uid && query.data) {
          map.set(uid, query.data);
        }
      });
    }
    return map;
  }, [progressQueries, learningPathUIDs]);

  // Transform learning paths with user progress
  const learningPathsWithProgress = useMemo(() => {
    if (propLearningPaths.length > 0) {
      // If prop learning paths provided, use them
      return propLearningPaths.map((path) => {
        // Try to find progress by matching title or URL
        // This is a fallback if UID matching doesn't work
        return path;
      });
    }

    if (!slider?.cards) return [];

    return slider.cards
      .map((cardItem: any): LearningPathData | null => {
        const learningPath = cardItem?.card?.[0];
        if (!learningPath) return null;

        const uid = learningPath.uid;
        const userProgress = progressMap.get(uid);
        
        const details = learningPath.learning_pathway_details || {};
        const courses = learningPath.courses ?? {};
        const courseList = learningPath.courses?.course || [];
        const courseCount = courseList.length;
        const videosCount =
          typeof details.videos_count === 'number' && details.videos_count > 0
            ? details.videos_count
            : getVideosCountFromCourses(courses);
        const quizzesCount = getCoursesWithQuizCount(courses);

        const stats = {
          courses: courseCount || 0,
          videos: videosCount || 0,
          quizzes: quizzesCount || 0,
          assessments: learningPath.is_assessment ? 1 : 0,
        };

        const certificateLabel = getLearningPathCertificateLabel(details.awards_certifications);

        const imageUrl = details.thumbnail?.url || learningPath.open_graph?.image?.url || '';
        const imageAlt = details.thumbnail?.title || learningPath.title || '';

        if (!imageUrl) return null;

        // Determine enrollment and progress
        const isEnrolled = userProgress?.isSubscribed ?? false;
        const progressPercentage = userProgress?.progress?.percentage ?? 0;
        const isCompleted = userProgress?.progress?.isCompleted;
        const lpAssessmentUnlocked = userProgress
          ? computeLpAssessmentUnlocked(userProgress)
          : false;
        const hasAssessment = learningPath.is_assessment === true;
        const baseUrl = learningPath.url || '#';
        const resumeLink =
          isEnrolled && hasAssessment && lpAssessmentUnlocked && !isCompleted
            ? `${baseUrl}/assessment`
            : isEnrolled
              ? baseUrl
              : undefined;

        return {
          image: {
            url: imageUrl,
            alt: imageAlt,
          },
          smallLabel: 'Learning Path',
          title: learningPath.title || '',
          description: details.description || learningPath.open_graph?.description || '',
          certificateLabel,
          stats,
          isEnrolled,
          progressPercentage: Math.round(progressPercentage),
          isCompleted,
          isLearningMaterialCompleted: lpAssessmentUnlocked,
          hasAssessment,
          enrollLink: learningPath.url || '#',
          resumeLink,
        };
      })
      .filter((item): item is LearningPathData => item !== null);
  }, [slider, progressMap, propLearningPaths]);

  return (
    <LearningPathSection
      learningPaths={learningPathsWithProgress}
      slider={slider}
    />
  );
}

